// src/admin/screens/borrowers/modals/AdjustCreditScoreModal.tsx
// Queues a credit-score override for super-admin review rather than
// writing to credit_scores directly. The server enforces the four-eye
// rule: admin_action_requests.requester ≠ reviewer, and the request
// must be in state 'pending' before anyone can flip it to 'approved'.
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import { useAuth } from '@admin/auth/useAuth';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { Json } from '@/lib/database.types';
import type { BorrowerDetail } from '@admin/hooks/useBorrower';

const schema = z.object({
  delta: z.coerce.number().int().min(-25).max(25),
  reason: z.string().min(10, 'At least 10 characters'),
});
type Values = z.infer<typeof schema>;

interface Props {
  borrower: BorrowerDetail;
  onClose: (refresh: boolean) => void;
}

export default function AdjustCreditScoreModal({ borrower, onClose }: Props) {
  const { admin } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  const submit = async (v: Values) => {
    if (!admin) return;
    setBusy(true);
    try {
      const { error } = await supabase.from('admin_action_requests').insert({
        action_type: 'adjust_credit_score',
        status: 'pending',
        target_borrower_id: borrower.id,
        request_payload: { delta: v.delta } as Json,
        requested_by: admin.id,
        request_notes: v.reason,
      });
      if (error) throw error;
      await recordAudit({
        action: 'action.review',
        target_type: 'borrower',
        target_id: borrower.id,
        payload: { delta: v.delta, reason: v.reason },
      });
      toast({ kind: 'info', msg: 'Adjustment submitted for super-admin review.' });
      // The request does not change anything observable on this screen
      // until a super-admin approves it — so no refetch needed.
      onClose(false);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  const name = borrower.display_name ?? borrower.full_name ?? 'borrower';

  return (
    <Modal open onClose={() => onClose(false)} title={`Adjust credit score — ${name}`}>
      <form
        onSubmit={handleSubmit(submit)}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <p className="small">
          Manual adjustment is reviewed by a super admin before it applies. Range: −25 to +25.
        </p>
        <FormField label="Delta (points)" htmlFor="score-delta" error={errors.delta?.message} required>
          <input id="score-delta" type="number" min={-25} max={25} {...register('delta')} />
        </FormField>
        <FormField label="Reason" htmlFor="score-reason" error={errors.reason?.message} required>
          <textarea id="score-reason" rows={3} {...register('reason')} />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={() => onClose(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Submit for review
          </Button>
        </div>
      </form>
    </Modal>
  );
}
